// mobile/screens/ReceiptScanScreen.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Image,
  Modal,
  TextInput,
  Platform,
  Linking,
} from 'react-native';
import axios from 'axios';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import { API_BASE } from '../utils';
import EnterpriseHeader from '../components/EnterpriseHeader';
import { PADIPRO } from '../components/padiproTheme';

const COLORS = {
  bg: PADIPRO.bg,
  surface: PADIPRO.surface,
  text: PADIPRO.text,
  muted: PADIPRO.muted,
  border: PADIPRO.border,
  green: PADIPRO.primary,
  greenDark: PADIPRO.primaryDark,
  greenSoft: PADIPRO.primarySoft,
  gold: PADIPRO.gold,
  goldSoft: PADIPRO.goldSoft,
  danger: PADIPRO.danger,
  dangerSoft: PADIPRO.dangerSoft,
  sky: PADIPRO.sky,
  skySoft: PADIPRO.skySoft,
};

const ACTIONS = [
  { key: 'use', label: 'Langsung Pakai', icon: 'flash-outline', hint: 'Masuk expense sekarang' },
  { key: 'inventory', label: 'Masuk Stok', icon: 'cube-outline', hint: 'Tambah inventory dulu' },
  { key: 'split', label: 'Split', icon: 'git-branch-outline', hint: 'Sebagian pakai, sisa stok' },
  { key: 'skip', label: 'Skip', icon: 'remove-circle-outline', hint: 'Jangan proses' },
];

const fmtIDR = (n) => `Rp ${Number(n || 0).toLocaleString('id-ID')}`;
const fmtNum = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return String(n).replace(/\.0+$/, '');
};
const parseNum = (v) => {
  const n = Number(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};
const isStockAction = (action, stockQty) => action === 'inventory' || (action === 'split' && Number(stockQty || 0) > 0);
const shortDate = (s) => {
  if (!s) return '-';
  const d = new Date(`${String(s).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(s);
  return new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }).format(d);
};
const describeProcessedItem = (item) => {
  if (item?.duplicate_skipped) {
    const dup = item.duplicate || {};
    return item?.description || `${item?.scanned_item_name || item?.raw_name || 'Item'} sudah pernah discan${dup.confirmed_at ? ` pada ${shortDate(dup.confirmed_at)}` : ''}. Tidak masuk stok dan biaya.`;
  }
  const inv = item?.inventory_change;
  const exp = item?.expense_change;
  if (inv?.description) return inv.description;
  if (exp?.description) return exp.description;
  return item?.description || `${item?.scanned_item_name || item?.raw_name || 'Item'} diproses.`;
};
const processedTitle = (item) => {
  if (item?.duplicate_skipped) return 'Duplikat diskip';
  const inv = item?.inventory_change;
  if (inv?.mode === 'merged_existing') return 'Digabung ke existing';
  if (inv?.mode === 'created_new') return 'Produk baru ditambah';
  if (item?.expense_change) return 'Masuk pengeluaran';
  if (item?.action === 'skip') return 'Diskip';
  return 'Diproses';
};
const processedIcon = (item) => {
  if (item?.duplicate_skipped) return 'alert-circle-outline';
  const inv = item?.inventory_change;
  if (inv?.mode === 'merged_existing') return 'git-merge-outline';
  if (inv?.mode === 'created_new') return 'add-circle-outline';
  if (item?.expense_change) return 'cash-outline';
  if (item?.action === 'skip') return 'remove-circle-outline';
  return 'checkmark-circle-outline';
};

const getDefaultAction = (item) => {
  const a = String(item?.selected_action || item?.suggested_action || '').toLowerCase();
  return ['use', 'inventory', 'split', 'skip'].includes(a) ? a : 'use';
};

const scanStatusText = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'confirmed') return 'Sudah disimpan';
  if (s === 'draft') return 'Belum dikonfirmasi';
  return status || 'Scan';
};

const scanStatusIcon = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'confirmed') return 'checkmark-circle-outline';
  if (s === 'draft') return 'time-outline';
  return 'receipt-outline';
};

const scanSourceMeta = (scan) => {
  const haystack = `${scan?.store_name || ''} ${scan?.item_names || ''}`.toLowerCase();
  if (haystack.includes('shopee')) {
    return {
      label: 'Shopee',
      icon: 'cart-outline',
      color: '#C2410C',
      bg: '#FFF1E8',
      border: '#FED7AA',
    };
  }
  if (haystack.includes('beringin')) {
    return {
      label: 'Kios Beringin',
      icon: 'business-outline',
      color: COLORS.greenDark,
      bg: PADIPRO.surfaceSunken,
      border: PADIPRO.border,
    };
  }
  if (haystack.includes('kios') || haystack.includes('ud.') || haystack.includes('toko')) {
    return {
      label: 'Kios',
      icon: 'business-outline',
      color: PADIPRO.goldDeep,
      bg: COLORS.goldSoft,
      border: 'rgba(166,124,0,0.32)',
    };
  }
  return {
    label: 'Struk',
    icon: 'receipt-outline',
    color: COLORS.muted,
    bg: PADIPRO.surfaceSunken,
    border: PADIPRO.border,
  };
};

const scanPhotoUrl = (scan) => {
  const url = scan?.image_url || scan?.image_path || '';
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${String(API_BASE).replace(/\/+$/, '')}${url.startsWith('/') ? '' : '/'}${url}`;
};

const normalizeFields = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  return list
    .map((x) => {
      const id = x.id ?? x.field_id ?? x.fieldId ?? null;
      const name = x.name ?? x.field_name ?? x.title ?? '';
      const area = x.area_rante ?? x.rante ?? x.luas_rante ?? x.rante_size ?? null;
      return {
        id: id != null ? Number(id) : null,
        name: String(name || ''),
        area_rante: area != null ? Number(area) : null,
      };
    })
    .filter((x) => x.id && x.name);
};

export default function ReceiptScanScreen({ route, navigation }) {
  const token = route?.params?.token;
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [draft, setDraft] = useState(null);
  const [items, setItems] = useState([]);
  const [fieldsList, setFieldsList] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [successModal, setSuccessModal] = useState(null);
  const [scanDuplicateNotice, setScanDuplicateNotice] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);

  const fetchFieldsCompat = useCallback(async () => {
    if (!token) return;
    setFieldsLoading(true);
    try {
      const rMin = await axios.get(`${API_BASE}/operations/fields-min`, { headers });
      const rowsMin = rMin?.data?.fields || rMin?.data?.items || rMin?.data || [];
      const normMin = normalizeFields(rowsMin);
      if (normMin.length) {
        setFieldsList(normMin);
        return;
      }

      const rOps = await axios.get(`${API_BASE}/operations/fields`, { headers });
      const rowsOps = rOps?.data?.fields || rOps?.data?.items || rOps?.data || [];
      const normOps = normalizeFields(rowsOps);
      if (normOps.length) {
        setFieldsList(normOps);
        return;
      }

      const rLegacy = await axios.get(`${API_BASE}/fields`, { headers });
      const rowsLegacy = rLegacy?.data?.fields || rLegacy?.data?.items || rLegacy?.data || [];
      setFieldsList(normalizeFields(rowsLegacy));
    } catch {
      setFieldsList([]);
    } finally {
      setFieldsLoading(false);
    }
  }, [headers, token]);

  useEffect(() => {
    fetchFieldsCompat();
  }, [fetchFieldsCompat]);

  const fetchRecentScans = useCallback(async () => {
    if (!token) return;
    setRecentLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/receipts/recent`, {
        params: { limit: 5 },
        headers,
      });
      setRecentScans(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch {
      setRecentScans([]);
    } finally {
      setRecentLoading(false);
    }
  }, [headers, token]);

  useEffect(() => {
    fetchRecentScans();
  }, [fetchRecentScans]);

  const totals = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        const action = getDefaultAction(item);
        const qty = Number(item.quantity || 0) || 0;
        const unitPrice = Number(item.unit_price || 0) || (qty > 0 ? Number(item.total || 0) / qty : 0);
        const total = Number(item.total || 0) || (qty * unitPrice);
        const useQty = action === 'split' ? Number(parseNum(item.use_qty) || 0) : qty;
        const stockQty = action === 'split' ? Number(parseNum(item.stock_qty) || 0) : qty;
        if (action === 'use') acc.expense += total;
        if (action === 'inventory') acc.stock += total;
        if (action === 'split') {
          acc.expense += Math.max(0, useQty) * unitPrice;
          acc.stock += Math.max(0, stockQty) * unitPrice;
        }
        if (action === 'skip') acc.skip += total;
        return acc;
      },
      { expense: 0, stock: 0, skip: 0 }
    );
  }, [items]);

  const pendingProductDecisions = useMemo(() => {
    return items.filter((item) => {
      const action = getDefaultAction(item);
      const stockQty = action === 'split' ? Number(parseNum(item.stock_qty) || 0) : Number(item.quantity || 0) || 0;
      return isStockAction(action, stockQty) && !item.product_confirmed;
    });
  }, [items]);

  const processableItems = useMemo(
    () => items.filter((it) => getDefaultAction(it) !== 'skip'),
    [items]
  );

  const scanDuplicateItems = useMemo(
    () => items.filter((item) => item.duplicate_warning || item.duplicate_skipped),
    [items]
  );

  const pickFromCamera = async () => {
    const perm = await ImagePicker.requestCameraPermissionsAsync();
    if (perm.status !== 'granted') return Alert.alert('Izin kamera diperlukan', 'Aktifkan izin kamera untuk foto struk.');
    const res = await ImagePicker.launchCameraAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.82,
      allowsEditing: false,
    });
    if (!res.canceled && res.assets?.[0]) {
      setPhoto(res.assets[0]);
      setDraft(null);
      setItems([]);
      setScanDuplicateNotice(null);
    }
  };

  const pickFromGallery = async () => {
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (perm.status !== 'granted') return Alert.alert('Izin galeri diperlukan', 'Aktifkan izin galeri untuk upload struk.');
    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.82,
      allowsEditing: false,
    });
    if (!res.canceled && res.assets?.[0]) {
      setPhoto(res.assets[0]);
      setDraft(null);
      setItems([]);
      setScanDuplicateNotice(null);
    }
  };

  const scanReceipt = async () => {
    if (!photo?.uri) return Alert.alert('Foto belum ada', 'Ambil foto atau pilih struk dulu.');

    try {
      setLoading(true);
      const form = new FormData();
      const filename = photo.fileName || `receipt-${Date.now()}.jpg`;
      const type = photo.mimeType || 'image/jpeg';

      if (Platform.OS === 'web') {
        if (photo.file) {
          form.append('receipt', photo.file, photo.file.name || filename);
        } else {
          const blobRes = await fetch(photo.uri);
          const blob = await blobRes.blob();
          const fileLike =
            typeof File !== 'undefined'
              ? new File([blob], filename, { type: blob.type || type })
              : blob;
          form.append('receipt', fileLike, filename);
        }
      } else {
        form.append('receipt', { uri: photo.uri, name: filename, type });
      }

      const res = await axios.post(`${API_BASE}/receipts/scan`, form, {
        headers,
        timeout: 70000,
      });

      const d = res.data?.draft || null;
      const list = Array.isArray(res.data?.items) ? res.data.items : [];
      setDraft(d);
      setItems(list.map((it) => {
        const selectedAction = getDefaultAction(it);
        const qty = fmtNum(it.quantity || 0);
        return {
          ...it,
          selected_action: selectedAction,
          product_mode: it.suggested_product_id ? 'existing' : 'new',
          selected_product_id: it.suggested_product_id || null,
          selected_product_name: it.suggested_product_name || '',
          product_confirmed: false,
          product_search: '',
          product_search_results: [],
          product_searching: false,
          use_qty: selectedAction === 'inventory' ? '0' : qty,
          stock_qty: selectedAction === 'inventory' ? qty : '0',
          selected_field_ids: [],
          split_method: 'area',
        };
      }));
      const duplicateItems = list.filter((it) => it.duplicate_warning || it.duplicate_skipped);
      if (duplicateItems.length) {
        setScanDuplicateNotice({ items: duplicateItems });
      } else {
        setScanDuplicateNotice(null);
      }
      fetchRecentScans();
    } catch (e) {
      const status = e?.response?.status;
      const server = e?.response?.data || {};
      const reqId = server?.reqId;
      const message = server?.error || e?.message || 'Sistem gagal membaca struk';
      Alert.alert(
        'Gagal scan',
        `${message}${status ? `\nHTTP: ${status}` : ''}${reqId ? `\nReqId: ${reqId}` : ''}`
      );
    } finally {
      setLoading(false);
    }
  };

  const updateItem = (itemId, patch) => {
    setItems((prev) => prev.map((it) => (it.item_id === itemId ? { ...it, ...patch } : it)));
  };

  const searchInventoryProduct = async (item, text) => {
    const q = String(text ?? '').trim();
    updateItem(item.item_id, { product_search: text, product_searching: q.length >= 2 });
    if (q.length < 2) {
      updateItem(item.item_id, { product_search_results: [], product_searching: false });
      return;
    }

    try {
      const res = await axios.get(`${API_BASE}/pesticides/search`, {
        params: { q, limit: 8 },
        headers,
      });
      updateItem(item.item_id, {
        product_search_results: Array.isArray(res.data?.items) ? res.data.items : [],
        product_searching: false,
      });
    } catch {
      updateItem(item.item_id, { product_search_results: [], product_searching: false });
    }
  };

  const chooseExistingProduct = (item, product) => {
    updateItem(item.item_id, {
      product_mode: 'existing',
      selected_product_id: product?.pesticide_id || product?.id || null,
      selected_product_name: product?.name || '',
      selected_product_stock: product?.stock ?? null,
      selected_product_unit: product?.unit || item.unit || null,
      product_confirmed: true,
      product_search: product?.name || '',
      product_search_results: [],
    });
  };

  const chooseNewProduct = (item) => {
    updateItem(item.item_id, {
      product_mode: 'new',
      selected_product_id: null,
      selected_product_name: '',
      selected_product_stock: null,
      selected_product_unit: null,
      product_confirmed: true,
      product_search_results: [],
    });
  };

  const setAction = (item, action) => {
    const qty = fmtNum(item.quantity || 0);
    const patch = { selected_action: action };
    if (action === 'split') {
      patch.use_qty = qty;
      patch.stock_qty = '0';
    }
    updateItem(item.item_id, patch);
  };

  const confirmDraft = async () => {
    if (!draft?.draft_id) return Alert.alert('Draft belum ada', 'Scan struk dulu sebelum konfirmasi.');
    if (!processableItems.length) return Alert.alert('Semua item duplikat', 'Tidak ada stok atau biaya yang perlu disimpan ulang.');
    if (pendingProductDecisions.length) {
      Alert.alert(
        'Cek keputusan produk dulu',
        [
          'Ada item stok yang belum dipilih mau gabung ke existing atau buat produk baru.',
          ...pendingProductDecisions.slice(0, 4).map((it, i) => `${i + 1}. ${it.raw_name || it.normalized_name || 'Item struk'}`),
          pendingProductDecisions.length > 4 ? `+${pendingProductDecisions.length - 4} item lain` : null,
        ].filter(Boolean).join('\n')
      );
      return;
    }

    try {
      setConfirming(true);
      const payload = {
        items: items.map((it) => ({
          item_id: it.item_id,
          action: getDefaultAction(it),
          product_mode: it.product_mode === 'existing' ? 'existing' : 'new',
          force_new: it.product_mode !== 'existing',
          product_confirmed: !!it.product_confirmed,
          product_id: it.product_mode === 'existing'
            ? (it.selected_product_id || it.suggested_product_id || null)
            : null,
          use_qty: parseNum(it.use_qty),
          stock_qty: parseNum(it.stock_qty),
          field_ids: Array.isArray(it.selected_field_ids) ? it.selected_field_ids : [],
          split_method: it.split_method || 'area',
        })),
      };
      const res = await axios.post(`${API_BASE}/receipts/drafts/${draft.draft_id}/confirm`, payload, { headers, timeout: 45000 });
      const summary = res.data?.summary || {};
      setSuccessModal({
        expenseTotal: Number(summary.expense_total || 0),
        expenseCount: Number(summary.expense_count || 0),
        inventoryCount: Number(summary.inventory_count || 0),
        duplicateSkippedCount: Number(summary.duplicate_skipped_count || 0),
        allocatedFieldRows: Number(summary.allocated_field_rows || 0),
        processedItems: Array.isArray(res.data?.processed) ? res.data.processed : [],
        processedCount: Array.isArray(res.data?.processed) ? res.data.processed.length : processableItems.length,
      });
      fetchRecentScans();
    } catch (e) {
      const data = e?.response?.data || {};
      if (data.code === 'DUPLICATE_RECEIPT_ITEM') {
        const dup = data.duplicate || {};
        Alert.alert(
          'Scan duplikat ditolak',
          [
            data.error || 'Item ini sudah pernah discan dalam 7 hari terakhir.',
            dup.product_name ? `Produk: ${dup.product_name}` : null,
            dup.store_name ? `Struk lama: ${dup.store_name}` : null,
            dup.confirmed_at ? `Waktu lama: ${shortDate(dup.confirmed_at)}` : null,
          ].filter(Boolean).join('\n')
        );
        return;
      }
      Alert.alert('Gagal konfirmasi', data.error || e?.message || 'Draft gagal diproses');
    } finally {
      setConfirming(false);
    }
  };

  const resetAfterSuccess = () => {
    setSuccessModal(null);
    setScanDuplicateNotice(null);
    setPhoto(null);
    setDraft(null);
    setItems([]);
  };

  const goToExpensesAfterSuccess = () => {
    setSuccessModal(null);
    navigation.navigate('ExpensesList', { token, refreshAt: Date.now() });
  };

  const toggleItemField = (item, fieldId) => {
    const fid = Number(fieldId);
    if (!fid) return;
    const selected = Array.isArray(item.selected_field_ids) ? item.selected_field_ids.map(Number) : [];
    const next = selected.includes(fid) ? selected.filter((id) => id !== fid) : [...selected, fid];
    updateItem(item.item_id, { selected_field_ids: next });
  };

  const selectAllItemFields = (item) => {
    updateItem(item.item_id, { selected_field_ids: fieldsList.map((f) => Number(f.id)).filter(Boolean) });
  };

  const clearItemFields = (item) => {
    updateItem(item.item_id, { selected_field_ids: [] });
  };

  const openRecentScanPhoto = async (scan) => {
    const url = scanPhotoUrl(scan);
    if (!url) return Alert.alert('Foto belum tersedia', 'Scan ini belum punya link foto.');
    try {
      const canOpen = await Linking.canOpenURL(url);
      if (!canOpen) throw new Error('Link tidak bisa dibuka');
      await Linking.openURL(url);
    } catch {
      Alert.alert('Gagal buka foto', url);
    }
  };

  const renderItem = (item, idx) => {
    const action = getDefaultAction(item);
    const activeAction = ACTIONS.find((a) => a.key === action) || ACTIONS[0];
    const qty = Number(item.quantity || 0) || 0;
    const unitPrice = Number(item.unit_price || 0) || (qty > 0 ? Number(item.total || 0) / qty : 0);
    const stockQty = action === 'split' ? Number(parseNum(item.stock_qty) || 0) : qty;
    const needsProductDecision = isStockAction(action, stockQty);
    const selectedProductName = item.selected_product_name || item.suggested_product_name || '';
    const selectedProductStock = item.selected_product_stock ?? item.suggested_product_stock ?? null;
    const selectedProductUnit = item.selected_product_unit || item.suggested_product_unit || item.unit || 'unit';
    const matchedProductName = selectedProductName || `Produk #${item.selected_product_id || item.suggested_product_id}`;
    const productSearchResults = Array.isArray(item.product_search_results) ? item.product_search_results : [];
    const hasExistingTarget = !!(item.selected_product_id || item.suggested_product_id);
    const productDecisionMode = item.product_mode === 'existing' && hasExistingTarget ? 'existing' : 'new';
    const productDecisionPending = needsProductDecision && !item.product_confirmed;
    const matched = needsProductDecision
      ? productDecisionMode === 'existing'
        ? `${productDecisionPending ? 'Saran sistem' : 'Pilihan'}: gabung ke "${matchedProductName}" dan tambah ${fmtNum(stockQty)} ${item.unit || selectedProductUnit}.`
        : `${productDecisionPending ? 'Belum dikonfirmasi' : 'Pilihan'}: buat produk baru "${item.normalized_name || item.raw_name || 'Produk struk'}" dengan stok awal ${fmtNum(stockQty)} ${item.unit || 'unit'}.`
      : item.suggested_product_id
        ? `Match existing: ${matchedProductName}. Tidak tambah stok kecuali pilih Masuk Stok/Split.`
        : 'Produk baru jika nanti dipilih masuk stok';
    const selectedFieldIds = Array.isArray(item.selected_field_ids) ? item.selected_field_ids.map(Number) : [];
    const canAssignFields = action === 'use' || action === 'split';
    const fieldPickerOpen = !!item.field_picker_open;
    const selectedFieldNames = fieldsList
      .filter((f) => selectedFieldIds.includes(Number(f.id)))
      .map((f) => f.name);
    const selectedPreview = selectedFieldNames.slice(0, 3);
    const selectedMore = Math.max(0, selectedFieldNames.length - selectedPreview.length);
    const isDuplicateScan = !!(item.duplicate_warning || item.duplicate_skipped);

    return (
      <View key={item.item_id || idx} style={[styles.itemCard, isDuplicateScan && styles.itemCardSkipped]}>
        <View style={styles.itemTop}>
          <View style={styles.itemNo}><Text style={styles.itemNoText}>{idx + 1}</Text></View>
          <View style={{ flex: 1 }}>
            <Text style={styles.itemName}>{item.raw_name || item.normalized_name || 'Item struk'}</Text>
            <Text style={styles.itemMeta}>{fmtNum(item.quantity)} {item.unit || 'unit'} x {fmtIDR(unitPrice)} · {fmtIDR(item.total || qty * unitPrice)}</Text>
          </View>
          <View style={isDuplicateScan ? styles.skippedPill : styles.categoryPill}>
            <Text style={isDuplicateScan ? styles.skippedPillText : styles.categoryPillText}>
              {isDuplicateScan ? 'Diskip' : (item.category || 'lainnya')}
            </Text>
          </View>
        </View>

        {isDuplicateScan && (
          <>
            <View style={styles.duplicateHeroBox}>
              <View style={styles.duplicateHeroIcon}>
                <Ionicons name="shield-checkmark-outline" size={22} color={PADIPRO.goldDeep} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.duplicateHeroTitle}>Sudah pernah discan</Text>
                <Text style={styles.duplicateHeroText}>
                  {item.duplicate_message || 'Item ini otomatis dilewati supaya stok dan biaya tidak tercatat dua kali.'}
                </Text>
              </View>
            </View>
            <View style={styles.duplicateOutcomeRow}>
              <View style={styles.duplicateOutcomeCell}>
                <Ionicons name="cube-outline" size={15} color={PADIPRO.goldDeep} />
                <Text style={styles.duplicateOutcomeText}>Stok tidak berubah</Text>
              </View>
              <View style={styles.duplicateOutcomeCell}>
                <Ionicons name="cash-outline" size={15} color={PADIPRO.goldDeep} />
                <Text style={styles.duplicateOutcomeText}>Biaya tidak masuk</Text>
              </View>
            </View>
          </>
        )}

        {isDuplicateScan && (
          <View style={styles.lockedDecisionStrip}>
            <Ionicons name="lock-closed-outline" size={15} color={PADIPRO.goldDeep} />
            <Text style={styles.lockedDecisionText}>Dikunci sebagai Skip otomatis. Item lain tetap bisa diproses.</Text>
          </View>
        )}

        {isDuplicateScan ? null : (
          <>

        <View style={styles.decisionStrip}>
          <View style={styles.decisionIcon}>
            <Ionicons name={activeAction.icon} size={15} color={COLORS.greenDark} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.decisionTitle}>{activeAction.label}</Text>
            <Text style={styles.decisionSub}>{activeAction.hint}</Text>
          </View>
          <Text style={styles.itemAmount}>{fmtIDR(item.total || qty * unitPrice)}</Text>
        </View>

        <View style={[styles.matchBox, productDecisionPending && styles.matchBoxWarning]}>
          <Ionicons
            name={productDecisionPending ? 'alert-circle-outline' : item.suggested_product_id ? 'checkmark-circle-outline' : 'search-outline'}
            size={16}
            color={productDecisionPending ? COLORS.gold : COLORS.greenDark}
          />
          <Text style={styles.matchText}>{matched}</Text>
        </View>

        {(item.duplicate_warning || item.duplicate_skipped) && (
          <View style={styles.scanDuplicateBox}>
            <View style={styles.scanDuplicateIcon}>
              <Ionicons name="shield-checkmark-outline" size={16} color={PADIPRO.goldDeep} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.scanDuplicateTitle}>Produk ini sudah pernah discan</Text>
              <Text style={styles.scanDuplicateText}>
                {item.duplicate_message || 'Item otomatis di-skip supaya tidak masuk stok dan biaya dua kali.'}
              </Text>
            </View>
          </View>
        )}

        {needsProductDecision && (
          <View style={styles.productDecisionBox}>
            <View style={styles.productDecisionHead}>
              <View style={styles.productDecisionIcon}>
                <Ionicons name="cube-outline" size={16} color={COLORS.greenDark} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.productDecisionTitle}>Keputusan produk inventory</Text>
                <Text style={styles.productDecisionSub}>Wajib pilih salah satu agar varian seperti Calcium Nitrat dan C4 Calcium Nutrient tidak tercampur.</Text>
              </View>
              <View style={[styles.reviewBadge, item.product_confirmed && styles.reviewBadgeDone]}>
                <Ionicons
                  name={item.product_confirmed ? 'checkmark-circle' : 'alert-circle'}
                  size={13}
                  color={item.product_confirmed ? COLORS.greenDark : PADIPRO.goldDeep}
                />
                <Text style={[styles.reviewBadgeText, item.product_confirmed && styles.reviewBadgeTextDone]}>
                  {item.product_confirmed ? 'OK' : 'Cek'}
                </Text>
              </View>
            </View>

            <View style={styles.productModeRow}>
              <TouchableOpacity
                onPress={() => {
                  if (item.suggested_product_id || item.selected_product_id) {
                    chooseExistingProduct(item, {
                      pesticide_id: item.selected_product_id || item.suggested_product_id,
                      name: selectedProductName || item.suggested_product_name,
                      stock: selectedProductStock,
                      unit: selectedProductUnit,
                    });
                  }
                }}
                disabled={!item.suggested_product_id && !item.selected_product_id}
                activeOpacity={0.88}
                style={[
                  styles.productModeBtn,
                  productDecisionMode === 'existing' && styles.productModeBtnActive,
                  (!item.suggested_product_id && !item.selected_product_id) && styles.productModeBtnDisabled,
                ]}
              >
                <Ionicons name="git-merge-outline" size={15} color={productDecisionMode === 'existing' ? '#fff' : COLORS.greenDark} />
                <Text style={[styles.productModeText, productDecisionMode === 'existing' && styles.productModeTextActive]}>Gabung Existing</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => chooseNewProduct(item)}
                activeOpacity={0.88}
                style={[styles.productModeBtn, productDecisionMode !== 'existing' && styles.productModeBtnActiveNew]}
              >
                <Ionicons name="add-circle-outline" size={15} color={productDecisionMode !== 'existing' ? '#fff' : COLORS.greenDark} />
                <Text style={[styles.productModeText, productDecisionMode !== 'existing' && styles.productModeTextActive]}>Buat Baru</Text>
              </TouchableOpacity>
            </View>

            {productDecisionMode === 'existing' ? (
              <View style={styles.existingTargetBox}>
                <Text style={styles.existingTargetLabel}>Target existing</Text>
                <Text style={styles.existingTargetName}>{selectedProductName || 'Belum pilih produk existing'}</Text>
                <Text style={styles.existingTargetMeta}>
                  {selectedProductStock != null ? `Stok sekarang ${fmtNum(selectedProductStock)} ${selectedProductUnit}` : 'Cek nama produk sebelum confirm'}
                  {' '}· akan tambah {fmtNum(stockQty)} {item.unit || selectedProductUnit}
                </Text>
              </View>
            ) : (
              <View style={styles.newProductBox}>
                <Text style={styles.newProductTitle}>Akan dibuat produk baru</Text>
                <Text style={styles.newProductDesc}>
                  Nama awal: {item.normalized_name || item.raw_name || 'Produk struk'} · stok awal {fmtNum(stockQty)} {item.unit || 'unit'}
                </Text>
              </View>
            )}

            <View style={styles.productSearchBox}>
              <Text style={styles.productSearchLabel}>Cari existing lain</Text>
              <TextInput
                value={String(item.product_search || '')}
                onChangeText={(v) => searchInventoryProduct(item, v)}
                placeholder="Ketik nama produk inventory..."
                placeholderTextColor="#8CA190"
                style={styles.productSearchInput}
              />
              {item.product_searching && <ActivityIndicator size="small" style={{ marginTop: 8 }} />}
              {!!productSearchResults.length && (
                <View style={styles.productResultList}>
                  {productSearchResults.map((p) => (
                    <TouchableOpacity key={`${item.item_id}-prod-${p.pesticide_id}`} style={styles.productResultRow} onPress={() => chooseExistingProduct(item, p)} activeOpacity={0.88}>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.productResultName}>{p.name}</Text>
                        <Text style={styles.productResultMeta}>Stok {fmtNum(p.stock)} {p.unit || 'unit'} · {p.category || 'inventory'}</Text>
                      </View>
                      <Ionicons name="arrow-forward-circle-outline" size={19} color={COLORS.greenDark} />
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>
          </View>
        )}

        <Text style={styles.actionCaption}>Tentukan perlakuan item</Text>
        <View style={styles.actionGrid}>
          {ACTIONS.map((a) => {
            const active = action === a.key;
            return (
              <TouchableOpacity key={a.key} style={[styles.actionBtn, active && styles.actionBtnActive]} onPress={() => setAction(item, a.key)} activeOpacity={0.88}>
                <Ionicons name={a.icon} size={15} color={active ? '#fff' : COLORS.greenDark} />
                <View style={{ flex: 1 }}>
                  <Text style={[styles.actionLabel, active && styles.actionLabelActive]}>{a.label}</Text>
                  <Text style={[styles.actionHint, active && styles.actionHintActive]} numberOfLines={1}>{a.hint}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>

        {action === 'split' && (
          <View style={styles.splitBox}>
            <View style={{ flex: 1 }}>
              <Text style={styles.inputLabel}>Dipakai sekarang</Text>
              <TextInput value={String(item.use_qty ?? '')} onChangeText={(v) => updateItem(item.item_id, { use_qty: v })} keyboardType="decimal-pad" style={styles.input} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.inputLabel}>Masuk stok</Text>
              <TextInput value={String(item.stock_qty ?? '')} onChangeText={(v) => updateItem(item.item_id, { stock_qty: v })} keyboardType="decimal-pad" style={styles.input} />
            </View>
          </View>
        )}

        {canAssignFields && (
          <View style={styles.fieldAssignBox}>
            <View style={styles.fieldAssignHead}>
              <View style={{ flex: 1 }}>
                <Text style={styles.fieldAssignTitle}>Assign ke lahan</Text>
                <Text style={styles.fieldAssignSub}>
                  {selectedFieldIds.length
                    ? `${selectedFieldIds.length} lahan dipilih`
                    : action === 'use'
                      ? 'Otomatis dibagi ke semua lahan saat disimpan'
                      : 'Opsional, kalau kosong masuk biaya umum musim'}
                </Text>
              </View>
              <TouchableOpacity
                onPress={() => updateItem(item.item_id, { field_picker_open: !fieldPickerOpen })}
                activeOpacity={0.88}
                style={styles.fieldOpenBtn}
              >
                <Text style={styles.fieldOpenText}>{fieldPickerOpen ? 'Tutup' : 'Pilih'}</Text>
                <Ionicons name={fieldPickerOpen ? 'chevron-up' : 'chevron-down'} size={15} color={COLORS.greenDark} />
              </TouchableOpacity>
            </View>

            <View style={styles.fieldCompactRow}>
              <View style={styles.fieldMethodBadge}>
                <Ionicons name={(item.split_method || 'area') === 'area' ? 'analytics-outline' : 'resize-outline'} size={13} color={COLORS.greenDark} />
                <Text style={styles.fieldMethodBadgeText}>{(item.split_method || 'area') === 'area' ? 'Bagi luas' : 'Bagi rata'}</Text>
              </View>
              {selectedPreview.length ? (
                <View style={styles.selectedPreviewRow}>
                  {selectedPreview.map((name) => (
                    <View key={`${item.item_id}-sel-${name}`} style={styles.selectedPreviewPill}>
                      <Text style={styles.selectedPreviewText} numberOfLines={1}>{name}</Text>
                    </View>
                  ))}
                  {!!selectedMore && (
                    <View style={styles.selectedMorePill}>
                      <Text style={styles.selectedMoreText}>+{selectedMore}</Text>
                    </View>
                  )}
                </View>
              ) : (
                <Text style={styles.fieldCompactHint}>{action === 'use' ? 'Semua lahan otomatis' : 'Belum pilih lahan'}</Text>
              )}
            </View>

            {fieldPickerOpen && (
              <>
                <View style={styles.methodRow}>
                  <TouchableOpacity
                    onPress={() => updateItem(item.item_id, { split_method: 'area' })}
                    activeOpacity={0.88}
                    style={[styles.methodChip, (item.split_method || 'area') === 'area' && styles.methodChipActive]}
                  >
                    <Text style={[styles.methodChipText, (item.split_method || 'area') === 'area' && styles.methodChipTextActive]}>Otomatis Luas</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => updateItem(item.item_id, { split_method: 'equal' })}
                    activeOpacity={0.88}
                    style={[styles.methodChip, item.split_method === 'equal' && styles.methodChipActive]}
                  >
                    <Text style={[styles.methodChipText, item.split_method === 'equal' && styles.methodChipTextActive]}>Rata</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => selectAllItemFields(item)} activeOpacity={0.88} style={styles.miniActionBtn}>
                    <Text style={styles.miniActionText}>Semua</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => clearItemFields(item)} activeOpacity={0.88} style={styles.miniGhostBtn}>
                    <Text style={styles.miniGhostText}>Reset</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={fetchFieldsCompat} activeOpacity={0.88} style={styles.miniGhostBtn}>
                    {fieldsLoading ? <ActivityIndicator size="small" /> : <Text style={styles.miniGhostText}>Refresh</Text>}
                  </TouchableOpacity>
                </View>

                {fieldsLoading && !fieldsList.length ? (
                  <View style={styles.fieldEmptyBox}><ActivityIndicator /></View>
                ) : fieldsList.length === 0 ? (
                  <View style={styles.fieldEmptyBox}>
                    <Text style={styles.fieldEmptyText}>List lahan belum tersedia</Text>
                  </View>
                ) : (
                  <View style={styles.fieldChipGrid}>
                    {fieldsList.map((f) => {
                      const checked = selectedFieldIds.includes(Number(f.id));
                      return (
                        <TouchableOpacity key={`${item.item_id}-${f.id}`} style={[styles.fieldChip, checked && styles.fieldChipActive]} onPress={() => toggleItemField(item, f.id)} activeOpacity={0.9}>
                          <View style={{ flex: 1 }}>
                            <Text style={[styles.fieldChipName, checked && styles.fieldChipNameActive]} numberOfLines={1}>{f.name}</Text>
                            <Text style={[styles.fieldChipMeta, checked && styles.fieldChipMetaActive]}>{f.area_rante != null ? `${f.area_rante} rante` : 'rante kosong'}</Text>
                          </View>
                          <Ionicons name={checked ? 'checkmark-circle' : 'ellipse-outline'} size={19} color={checked ? '#fff' : '#78907d'} />
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                )}
              </>
            )}
          </View>
        )}
          </>
        )}
      </View>
    );
  };

  const latestScan = recentScans[0] || null;
  const olderRecentScans = recentScans.slice(1, 5);

  return (
      <View style={styles.screen}>
        <View style={styles.glowA} />
        <View style={styles.glowB} />
        <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
        <EnterpriseHeader title="Scan Struk" subtitle="Foto struk, review item, lalu simpan ke pengeluaran atau stok" />

        <View style={styles.heroCard}>
          <View style={styles.heroTop}>
            <View style={styles.heroIcon}><Ionicons name="receipt-outline" size={25} color={COLORS.greenDark} /></View>
            <View style={{ flex: 1 }}>
              <Text style={styles.heroKicker}>Review otomatis</Text>
              <Text style={styles.heroTitle}>Struk jadi rapi sebelum masuk biaya</Text>
            </View>
          </View>
          <Text style={styles.heroSub}>Barang yang langsung dipakai masuk pengeluaran. Barang yang belum dipakai masuk stok dulu agar biaya tidak double.</Text>
          <View style={styles.heroSteps}>
            <View style={styles.stepPill}><Text style={styles.stepText}>1 Foto</Text></View>
            <View style={styles.stepPill}><Text style={styles.stepText}>2 Review</Text></View>
            <View style={styles.stepPill}><Text style={styles.stepText}>3 Simpan</Text></View>
          </View>
        </View>

        <View style={styles.photoCard}>
          {photo?.uri ? (
            <Image source={{ uri: photo.uri }} style={styles.preview} />
          ) : (
            <View style={styles.emptyPhoto}>
              <Ionicons name="camera-outline" size={38} color={COLORS.greenDark} />
              <Text style={styles.emptyPhotoTitle}>Belum ada foto struk</Text>
              <Text style={styles.emptyPhotoSub}>Pastikan struk terang, tidak blur, dan seluruh item terlihat.</Text>
            </View>
          )}

          <View style={styles.pickRow}>
            <TouchableOpacity style={[styles.pickBtn, styles.pickPrimary]} onPress={pickFromCamera} activeOpacity={0.88}>
              <Ionicons name="camera-outline" size={17} color="#fff" />
              <Text style={styles.pickPrimaryText}>Foto Struk</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.pickBtn} onPress={pickFromGallery} activeOpacity={0.88}>
              <Ionicons name="image-outline" size={17} color={COLORS.greenDark} />
              <Text style={styles.pickText}>Galeri</Text>
            </TouchableOpacity>
          </View>

          <TouchableOpacity style={[styles.scanBtn, (!photo?.uri || loading) && styles.disabledBtn]} disabled={!photo?.uri || loading} onPress={scanReceipt} activeOpacity={0.9}>
            {loading ? <ActivityIndicator color="#fff" /> : <Ionicons name="scan-outline" size={18} color="#fff" />}
            <Text style={styles.scanText}>{loading ? 'Membaca struk...' : 'Baca Struk'}</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.recentPanel}>
          <View style={styles.recentPanelHead}>
            <View style={styles.recentTitleWrap}>
              <View style={styles.recentHeadIcon}>
                <Ionicons name="time-outline" size={17} color={COLORS.greenDark} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.recentKicker}>Riwayat scan</Text>
                <Text style={styles.recentTitle}>Scan terakhir</Text>
              </View>
            </View>
            <TouchableOpacity style={styles.recentRefreshBtn} onPress={fetchRecentScans} activeOpacity={0.85}>
              {recentLoading ? (
                <ActivityIndicator size="small" color={COLORS.greenDark} />
              ) : (
                <Ionicons name="refresh-outline" size={17} color={COLORS.greenDark} />
              )}
            </TouchableOpacity>
          </View>

          {latestScan ? (
            <>
              <View style={styles.latestScanCard}>
                <View style={styles.latestThumbWrap}>
                  {scanPhotoUrl(latestScan) ? (
                    <Image source={{ uri: scanPhotoUrl(latestScan) }} style={styles.latestThumb} />
                  ) : (
                    <View style={styles.latestThumbEmpty}>
                      <Ionicons name="image-outline" size={24} color={COLORS.muted} />
                    </View>
                  )}
                  <View style={styles.latestStatusDot}>
                    <Ionicons name={scanStatusIcon(latestScan.status)} size={13} color="#fff" />
                  </View>
                </View>

                <View style={styles.latestScanBody}>
                  <Text style={styles.latestStore} numberOfLines={1}>{latestScan.store_name || 'Scan struk'}</Text>
                  <View style={styles.latestBadgeRow}>
                    {(() => {
                      const src = scanSourceMeta(latestScan);
                      return (
                        <View style={[styles.sourcePill, { backgroundColor: src.bg, borderColor: src.border }]}>
                          <Ionicons name={src.icon} size={11} color={src.color} />
                          <Text style={[styles.sourcePillText, { color: src.color }]}>{src.label}</Text>
                        </View>
                      );
                    })()}
                    <View style={styles.latestStatusPill}>
                      <Text style={styles.latestStatusText}>{scanStatusText(latestScan.status)}</Text>
                    </View>
                  </View>
                  <Text style={styles.latestMeta}>
                    {shortDate(latestScan.receipt_date || latestScan.created_at)} · {Number(latestScan.item_count || 0)} item
                  </Text>
                  <Text style={styles.latestItems} numberOfLines={2}>
                    {latestScan.item_names || 'Item belum terbaca'}
                  </Text>
                  <View style={styles.latestActionRow}>
                    <View>
                      <Text style={styles.latestTotalLabel}>Total tersimpan</Text>
                      <Text style={styles.latestTotalValue}>{fmtIDR(latestScan.expense_total || latestScan.grand_total || 0)}</Text>
                    </View>
                    <TouchableOpacity style={styles.photoLinkBtn} onPress={() => openRecentScanPhoto(latestScan)} activeOpacity={0.88}>
                      <Ionicons name="open-outline" size={15} color="#fff" />
                      <Text style={styles.photoLinkText}>Buka Foto</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>

              {!!olderRecentScans.length && (
                <View style={styles.miniScanList}>
                  {olderRecentScans.map((scan) => (
                    <View key={scan.draft_id} style={styles.miniScanRow}>
                      <View style={styles.miniScanIcon}>
                        <Ionicons name={scanStatusIcon(scan.status)} size={14} color={COLORS.greenDark} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.miniScanTitle} numberOfLines={1}>{scan.store_name || 'Scan struk'}</Text>
                        <View style={styles.miniScanMetaRow}>
                          {(() => {
                            const src = scanSourceMeta(scan);
                            return (
                              <View style={[styles.miniSourcePill, { backgroundColor: src.bg, borderColor: src.border }]}>
                                <Ionicons name={src.icon} size={10} color={src.color} />
                                <Text style={[styles.miniSourcePillText, { color: src.color }]} numberOfLines={1}>{src.label}</Text>
                              </View>
                            );
                          })()}
                          <Text style={styles.miniScanSub} numberOfLines={1}>{shortDate(scan.receipt_date || scan.created_at)} · {fmtIDR(scan.expense_total || scan.grand_total || 0)}</Text>
                        </View>
                      </View>
                      <TouchableOpacity style={styles.miniPhotoBtn} onPress={() => openRecentScanPhoto(scan)} activeOpacity={0.85}>
                        <Ionicons name="image-outline" size={15} color={COLORS.greenDark} />
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              )}
            </>
          ) : (
            <View style={styles.recentEmpty}>
              <Ionicons name="receipt-outline" size={20} color={COLORS.muted} />
              <Text style={styles.recentEmptyText}>{recentLoading ? 'Mengambil scan terakhir...' : 'Belum ada riwayat scan.'}</Text>
            </View>
          )}
        </View>

        {!!draft && (
          <View style={styles.resultHead}>
            <View style={styles.resultIcon}>
              <Ionicons name="checkmark-done-outline" size={18} color={COLORS.greenDark} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.resultKicker}>Hasil scan</Text>
              <Text style={styles.resultTitle}>{draft.store_name || 'Struk terdeteksi'}</Text>
              <Text style={styles.resultSub}>{draft.receipt_date || 'Tanggal belum terbaca'} · {items.length} item</Text>
            </View>
            <View style={styles.totalPill}><Text style={styles.totalPillText}>{fmtIDR(draft.grand_total || 0)}</Text></View>
          </View>
        )}

        {!!draft && (
          <View style={styles.routePanel}>
            <View style={styles.routeCell}>
              <View style={styles.routeIcon}>
                <Ionicons name="today-outline" size={15} color={COLORS.greenDark} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.routeLabel}>Tanggal Expense</Text>
                <Text style={styles.routeValue}>{shortDate(draft.receipt_date)}</Text>
              </View>
            </View>
            <View style={styles.routeDivider} />
            <View style={styles.routeCell}>
              <View style={styles.routeIcon}>
                <Ionicons name="leaf-outline" size={15} color={COLORS.greenDark} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.routeLabel}>Musim</Text>
                <Text style={styles.routeValue}>{draft.season_name || 'Musim aktif'}</Text>
              </View>
            </View>
            {!!draft.raw_receipt_date && draft.raw_receipt_date !== draft.receipt_date && (
              <View style={styles.ocrNote}>
                <Ionicons name="scan-outline" size={14} color={COLORS.gold} />
                <Text style={styles.ocrNoteText}>OCR membaca {shortDate(draft.raw_receipt_date)}. Diposting sebagai {shortDate(draft.receipt_date)}.</Text>
              </View>
            )}
          </View>
        )}

        {!!items.length && (
          <View style={styles.summaryGrid}>
            <View style={styles.summaryBox}>
              <View style={styles.summaryIcon}><Ionicons name="cash-outline" size={16} color={COLORS.greenDark} /></View>
              <Text style={styles.summaryLabel}>Masuk Pengeluaran</Text>
              <Text style={styles.summaryValue}>{fmtIDR(totals.expense)}</Text>
            </View>
            <View style={styles.summaryBox}>
              <View style={styles.summaryIcon}><Ionicons name="cube-outline" size={16} color={COLORS.greenDark} /></View>
              <Text style={styles.summaryLabel}>Masuk Stok</Text>
              <Text style={styles.summaryValue}>{fmtIDR(totals.stock)}</Text>
            </View>
          </View>
        )}

        {!!scanDuplicateItems.length && (
          <View style={styles.duplicateSummaryBanner}>
            <View style={styles.duplicateSummaryIcon}>
              <Ionicons name="shield-checkmark-outline" size={20} color={PADIPRO.goldDeep} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.duplicateSummaryTitle}>{scanDuplicateItems.length} item sudah pernah discan</Text>
              <Text style={styles.duplicateSummaryText}>Sudah otomatis diskip. Stok dan biaya tidak bertambah untuk item tersebut.</Text>
            </View>
          </View>
        )}

        {!!pendingProductDecisions.length && (
          <View style={styles.reviewReminder}>
            <Ionicons name="alert-circle-outline" size={18} color={PADIPRO.goldDeep} />
            <Text style={styles.reviewReminderText}>
              {pendingProductDecisions.length} item stok perlu dicek: pilih Gabung Existing kalau benar produk lama, atau Buat Baru kalau beda varian.
            </Text>
          </View>
        )}

        {items.map(renderItem)}

        {!!items.length && (
          <TouchableOpacity
            style={[styles.confirmBtn, (confirming || !processableItems.length) && styles.disabledBtn]}
            disabled={confirming || !processableItems.length}
            onPress={confirmDraft}
            activeOpacity={0.9}
          >
            {confirming ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Ionicons name={processableItems.length ? 'checkmark-done-outline' : 'shield-checkmark-outline'} size={19} color="#fff" />
            )}
            <Text style={styles.confirmText}>
              {confirming ? 'Memproses...' : processableItems.length ? 'Konfirmasi & Simpan' : 'Semua Item Duplikat'}
            </Text>
          </TouchableOpacity>
        )}

        <Text style={styles.footnote}>Data baru masuk pengeluaran atau stok setelah tombol konfirmasi ditekan.</Text>
      </ScrollView>

      <Modal visible={!!scanDuplicateNotice} transparent animationType="fade" onRequestClose={() => setScanDuplicateNotice(null)}>
        <View style={styles.noticeMask}>
          <View style={styles.noticeCard}>
            <View style={styles.noticeIconWrap}>
              <Ionicons name="shield-checkmark-outline" size={36} color={PADIPRO.goldDeep} />
            </View>
            <Text style={styles.noticeTitle}>Double stok/biaya dicegah</Text>
            <Text style={styles.noticeSub}>
              {scanDuplicateNotice?.items?.length || 0} item sudah pernah discan. Item itu otomatis diskip dan tidak akan masuk stok atau pengeluaran.
            </Text>

            <View style={styles.noticeList}>
              {(scanDuplicateNotice?.items || []).slice(0, 4).map((it, idx) => (
                <View key={it.item_id || `${idx}`} style={styles.noticeListRow}>
                  <View style={styles.noticeListNo}>
                    <Text style={styles.noticeListNoText}>{idx + 1}</Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.noticeListName} numberOfLines={2}>{it.raw_name || it.normalized_name || 'Item struk'}</Text>
                    <Text style={styles.noticeListMeta}>{fmtNum(it.quantity)} {it.unit || 'unit'} tidak diproses</Text>
                  </View>
                </View>
              ))}
              {(scanDuplicateNotice?.items || []).length > 4 && (
                <Text style={styles.noticeMore}>+{scanDuplicateNotice.items.length - 4} item lain juga diskip.</Text>
              )}
            </View>

            <TouchableOpacity style={styles.noticePrimaryBtn} onPress={() => setScanDuplicateNotice(null)} activeOpacity={0.9}>
              <Text style={styles.noticePrimaryText}>Mengerti</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal visible={!!successModal} transparent animationType="fade" onRequestClose={resetAfterSuccess}>
        <View style={styles.successMask}>
          <View style={styles.successCard}>
            <View style={styles.successIconWrap}>
              <Ionicons name="checkmark-circle" size={42} color={COLORS.greenDark} />
            </View>
            <Text style={styles.successTitle}>Struk berhasil diproses</Text>
            <Text style={styles.successSub}>
              {successModal?.duplicateSkippedCount
                ? `${successModal.duplicateSkippedCount} item duplikat otomatis diskip. Item lain tetap diproses.`
                : 'Pengeluaran dan stok sudah diperbarui sesuai keputusan item.'}
            </Text>

            <View style={styles.successStats}>
              <View style={styles.successStatBox}>
                <Text style={styles.successStatLabel}>Pengeluaran</Text>
                <Text style={styles.successStatValue}>{fmtIDR(successModal?.expenseTotal || 0)}</Text>
              </View>
              <View style={styles.successStatBox}>
                <Text style={styles.successStatLabel}>Item Stok</Text>
                <Text style={styles.successStatValue}>{successModal?.inventoryCount || 0}</Text>
              </View>
            </View>

            <View style={styles.successNote}>
              <Ionicons name="sparkles-outline" size={16} color={COLORS.gold} />
              <Text style={styles.successNoteText}>
                {successModal?.processedCount || 0} item dicek ke {draft?.season_name || 'musim aktif'} tanggal {shortDate(draft?.receipt_date)}. {successModal?.allocatedFieldRows || 0} alokasi lahan dibuat.
              </Text>
            </View>

            {!!successModal?.duplicateSkippedCount && (
              <View style={styles.duplicateNotice}>
                <View style={styles.duplicateNoticeIcon}>
                  <Ionicons name="shield-checkmark-outline" size={17} color={PADIPRO.goldDeep} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.duplicateNoticeTitle}>Double dicegah: {successModal?.duplicateSkippedCount || 0} item</Text>
                  <Text style={styles.duplicateNoticeText}>
                    Item yang sama dalam 7 hari atau scan yang sama otomatis diskip. Stok dan biaya tidak berubah untuk item itu.
                  </Text>
                </View>
              </View>
            )}

            {!!successModal?.processedItems?.length && (
              <View style={styles.processedPanel}>
                <Text style={styles.processedPanelTitle}>Detail hasil proses</Text>
                {successModal.processedItems.slice(0, 6).map((it, idx) => (
                  <View key={it.item_id || `${idx}`} style={styles.processedRow}>
                    <View style={styles.processedIcon}>
                      <Ionicons name={processedIcon(it)} size={15} color={it.duplicate_skipped ? COLORS.gold : COLORS.greenDark} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.processedTitle}>{processedTitle(it)}</Text>
                      <Text style={styles.processedDesc} numberOfLines={3}>{describeProcessedItem(it)}</Text>
                    </View>
                  </View>
                ))}
                {successModal.processedItems.length > 6 && (
                  <Text style={styles.processedMore}>+{successModal.processedItems.length - 6} item lain juga diproses.</Text>
                )}
              </View>
            )}

            <View style={styles.successActions}>
              <TouchableOpacity style={[styles.successBtn, styles.successGhost]} onPress={resetAfterSuccess} activeOpacity={0.88}>
                <Text style={styles.successGhostText}>Scan Lagi</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.successBtn, styles.successPrimary]} onPress={goToExpensesAfterSuccess} activeOpacity={0.9}>
                <Text style={styles.successPrimaryText}>Lihat Pengeluaran</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const shadow = Platform.select({
  android: { elevation: 2 },
  ios: { shadowColor: '#123521', shadowOpacity: 0.08, shadowRadius: 12, shadowOffset: { width: 0, height: 6 } },
  default: {},
});

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: COLORS.bg },
  glowA: { position: 'absolute', top: -80, right: -70, width: 240, height: 240, borderRadius: 999, backgroundColor: 'rgba(31,138,76,0.13)' },
  glowB: { position: 'absolute', top: 210, left: -120, width: 250, height: 250, borderRadius: 999, backgroundColor: 'rgba(184,138,43,0.11)' },
  content: { padding: 14, paddingBottom: 42 },
  heroCard: { marginTop: 12, borderRadius: 26, padding: 18, backgroundColor: '#FDFCF4', borderWidth: 1, borderColor: '#E7DFC0', ...shadow },
  heroTop: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  heroIcon: { width: 50, height: 50, borderRadius: 18, backgroundColor: COLORS.greenSoft, borderWidth: 1, borderColor: PADIPRO.border, alignItems: 'center', justifyContent: 'center' },
  heroKicker: { color: COLORS.gold, fontWeight: '900', fontSize: 12, letterSpacing: 0.3, textTransform: 'uppercase' },
  heroTitle: { color: COLORS.text, fontSize: 21, fontWeight: '900', letterSpacing: -0.45, lineHeight: 25 },
  heroSub: { marginTop: 7, color: COLORS.muted, fontWeight: '700', lineHeight: 20 },
  heroSteps: { marginTop: 13, flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  stepPill: { borderWidth: 1, borderColor: '#D9E8D8', backgroundColor: '#fff', borderRadius: 999, paddingHorizontal: 10, paddingVertical: 6 },
  stepText: { color: COLORS.greenDark, fontWeight: '900', fontSize: 12 },
  photoCard: { marginTop: 14, padding: 12, borderRadius: 22, backgroundColor: COLORS.surface, borderWidth: 1, borderColor: COLORS.border, ...shadow },
  preview: { width: '100%', height: 220, borderRadius: 18, backgroundColor: PADIPRO.surfaceSunken },
  emptyPhoto: { height: 210, borderRadius: 18, borderWidth: 1, borderColor: PADIPRO.border, borderStyle: 'dashed', backgroundColor: PADIPRO.surfaceSunken, alignItems: 'center', justifyContent: 'center', padding: 18 },
  emptyPhotoTitle: { marginTop: 10, color: COLORS.text, fontWeight: '900', fontSize: 16 },
  emptyPhotoSub: { marginTop: 5, color: COLORS.muted, fontWeight: '700', textAlign: 'center', lineHeight: 19 },
  pickRow: { flexDirection: 'row', gap: 10, marginTop: 12 },
  pickBtn: { flex: 1, minHeight: 46, borderRadius: 15, borderWidth: 1, borderColor: PADIPRO.border, backgroundColor: PADIPRO.surfaceSunken, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 8 },
  pickPrimary: { backgroundColor: COLORS.green, borderColor: COLORS.greenDark },
  pickText: { color: COLORS.greenDark, fontWeight: '900' },
  pickPrimaryText: { color: '#fff', fontWeight: '900' },
  scanBtn: { marginTop: 12, minHeight: 52, borderRadius: 18, backgroundColor: COLORS.greenDark, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 8 },
  scanText: { color: '#fff', fontWeight: '900', fontSize: 15 },
  recentPanel: { marginTop: 14, padding: 13, borderRadius: 22, backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: COLORS.border, ...shadow },
  recentPanelHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  recentTitleWrap: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10 },
  recentHeadIcon: { width: 34, height: 34, borderRadius: 13, backgroundColor: COLORS.greenSoft, borderWidth: 1, borderColor: PADIPRO.border, alignItems: 'center', justifyContent: 'center' },
  recentKicker: { color: COLORS.gold, fontWeight: '900', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.25 },
  recentTitle: { marginTop: 1, color: COLORS.text, fontWeight: '900', fontSize: 16 },
  recentRefreshBtn: { width: 36, height: 36, borderRadius: 13, backgroundColor: PADIPRO.surfaceSunken, borderWidth: 1, borderColor: PADIPRO.border, alignItems: 'center', justifyContent: 'center' },
  latestScanCard: { marginTop: 12, flexDirection: 'row', gap: 12, borderRadius: 18, padding: 10, backgroundColor: PADIPRO.surfaceSunken, borderWidth: 1, borderColor: PADIPRO.border },
  latestThumbWrap: { width: 78, height: 96, borderRadius: 16, backgroundColor: PADIPRO.surfaceSunken },
  latestThumb: { width: '100%', height: '100%', borderRadius: 16, backgroundColor: PADIPRO.surfaceSunken },
  latestThumbEmpty: { flex: 1, borderRadius: 16, alignItems: 'center', justifyContent: 'center', backgroundColor: '#EEF3ED' },
  latestStatusDot: { position: 'absolute', right: -4, bottom: -4, width: 25, height: 25, borderRadius: 10, backgroundColor: COLORS.greenDark, borderWidth: 2, borderColor: '#fff', alignItems: 'center', justifyContent: 'center' },
  latestScanBody: { flex: 1, minWidth: 0 },
  latestTopLine: { flexDirection: 'row', alignItems: 'center', gap: 7 },
  latestStore: { flex: 1, minWidth: 0, color: COLORS.text, fontWeight: '900', fontSize: 15 },
  latestBadgeRow: { marginTop: 5, flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6 },
  sourcePill: { minHeight: 24, paddingHorizontal: 8, borderRadius: 999, borderWidth: 1, flexDirection: 'row', alignItems: 'center', gap: 4 },
  sourcePillText: { fontWeight: '900', fontSize: 10.5 },
  latestStatusPill: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 999, backgroundColor: COLORS.goldSoft, borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)' },
  latestStatusText: { color: PADIPRO.goldDeep, fontWeight: '900', fontSize: 10.5 },
  latestMeta: { marginTop: 4, color: COLORS.muted, fontWeight: '800', fontSize: 12 },
  latestItems: { marginTop: 5, color: COLORS.text, fontWeight: '800', fontSize: 12.5, lineHeight: 17 },
  latestActionRow: { marginTop: 9, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  latestTotalLabel: { color: COLORS.muted, fontWeight: '900', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.2 },
  latestTotalValue: { marginTop: 2, color: COLORS.greenDark, fontWeight: '900', fontSize: 15 },
  photoLinkBtn: { minHeight: 36, paddingHorizontal: 11, borderRadius: 13, backgroundColor: COLORS.greenDark, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 },
  photoLinkText: { color: '#fff', fontWeight: '900', fontSize: 12 },
  miniScanList: { marginTop: 10, gap: 8 },
  miniScanRow: { minHeight: 48, borderRadius: 15, paddingHorizontal: 9, paddingVertical: 8, backgroundColor: PADIPRO.goldSoft, borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', flexDirection: 'row', alignItems: 'center', gap: 8 },
  miniScanIcon: { width: 29, height: 29, borderRadius: 11, backgroundColor: '#fff', borderWidth: 1, borderColor: PADIPRO.border, alignItems: 'center', justifyContent: 'center' },
  miniScanTitle: { color: COLORS.text, fontWeight: '900', fontSize: 12.5 },
  miniScanMetaRow: { marginTop: 3, flexDirection: 'row', alignItems: 'center', gap: 6 },
  miniSourcePill: { maxWidth: 98, minHeight: 21, paddingHorizontal: 7, borderRadius: 999, borderWidth: 1, flexDirection: 'row', alignItems: 'center', gap: 3 },
  miniSourcePillText: { flexShrink: 1, fontWeight: '900', fontSize: 10 },
  miniScanSub: { marginTop: 2, color: COLORS.muted, fontWeight: '800', fontSize: 11.5 },
  miniPhotoBtn: { width: 34, height: 34, borderRadius: 13, backgroundColor: '#fff', borderWidth: 1, borderColor: PADIPRO.border, alignItems: 'center', justifyContent: 'center' },
  recentEmpty: { marginTop: 12, minHeight: 58, borderRadius: 16, borderWidth: 1, borderStyle: 'dashed', borderColor: PADIPRO.border, backgroundColor: PADIPRO.surfaceSunken, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 12 },
  recentEmptyText: { color: COLORS.muted, fontWeight: '800', fontSize: 12.5 },
  disabledBtn: { opacity: 0.65 },
  resultHead: { marginTop: 16, padding: 14, borderRadius: 22, backgroundColor: COLORS.greenSoft, borderWidth: 1, borderColor: PADIPRO.border, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 10 },
  resultIcon: { width: 36, height: 36, borderRadius: 13, backgroundColor: '#fff', borderWidth: 1, borderColor: PADIPRO.border, alignItems: 'center', justifyContent: 'center' },
  resultKicker: { color: COLORS.greenDark, fontWeight: '900', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3 },
  resultTitle: { color: COLORS.text, fontWeight: '900', fontSize: 16 },
  resultSub: { marginTop: 3, color: COLORS.muted, fontWeight: '700' },
  totalPill: { paddingHorizontal: 11, paddingVertical: 8, borderRadius: 999, backgroundColor: '#fff', borderWidth: 1, borderColor: PADIPRO.border },
  totalPillText: { color: COLORS.greenDark, fontWeight: '900' },
  routePanel: { marginTop: 10, padding: 12, borderRadius: 20, backgroundColor: PADIPRO.goldSoft, borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', ...shadow },
  routeCell: { flexDirection: 'row', alignItems: 'center', gap: 9, paddingVertical: 5 },
  routeIcon: { width: 31, height: 31, borderRadius: 12, backgroundColor: PADIPRO.surfaceSunken, borderWidth: 1, borderColor: PADIPRO.border, alignItems: 'center', justifyContent: 'center' },
  routeLabel: { color: COLORS.muted, fontWeight: '900', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.25 },
  routeValue: { marginTop: 2, color: COLORS.text, fontWeight: '900', fontSize: 14 },
  routeDivider: { height: 1, backgroundColor: '#EFE6C8', marginVertical: 6 },
  ocrNote: { marginTop: 8, borderRadius: 15, padding: 10, backgroundColor: COLORS.goldSoft, borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', flexDirection: 'row', alignItems: 'flex-start', gap: 7 },
  ocrNoteText: { flex: 1, color: PADIPRO.goldDeep, fontWeight: '800', lineHeight: 17, fontSize: 12 },
  summaryGrid: { flexDirection: 'row', gap: 10, marginTop: 12 },
  summaryBox: { flex: 1, padding: 13, borderRadius: 20, borderWidth: 1, borderColor: COLORS.border, backgroundColor: '#fff', ...shadow },
  summaryIcon: { width: 30, height: 30, borderRadius: 11, backgroundColor: COLORS.goldSoft, borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', alignItems: 'center', justifyContent: 'center', marginBottom: 8 },
  summaryLabel: { color: COLORS.muted, fontWeight: '800', fontSize: 12 },
  summaryValue: { marginTop: 5, color: COLORS.text, fontWeight: '900', fontSize: 15 },
  duplicateSummaryBanner: { marginTop: 12, padding: 13, borderRadius: 20, backgroundColor: COLORS.goldSoft, borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', flexDirection: 'row', alignItems: 'flex-start', gap: 10, ...shadow },
  duplicateSummaryIcon: { width: 40, height: 40, borderRadius: 15, backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', alignItems: 'center', justifyContent: 'center' },
  duplicateSummaryTitle: { color: PADIPRO.goldDeep, fontWeight: '900', fontSize: 14 },
  duplicateSummaryText: { marginTop: 3, color: PADIPRO.goldDeep, fontWeight: '800', fontSize: 12, lineHeight: 17 },
  reviewReminder: { marginTop: 11, borderRadius: 18, padding: 12, backgroundColor: COLORS.goldSoft, borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  reviewReminderText: { flex: 1, color: PADIPRO.goldDeep, fontWeight: '800', fontSize: 12.5, lineHeight: 18 },
  itemCard: { marginTop: 12, padding: 13, borderRadius: 24, backgroundColor: COLORS.surface, borderWidth: 1, borderColor: COLORS.border, ...shadow },
  itemCardSkipped: { backgroundColor: PADIPRO.goldSoft, borderColor: 'rgba(166,124,0,0.32)' },
  itemTop: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  itemNo: { width: 30, height: 30, borderRadius: 11, backgroundColor: COLORS.goldSoft, borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', alignItems: 'center', justifyContent: 'center' },
  itemNoText: { color: PADIPRO.goldDeep, fontWeight: '900' },
  itemName: { color: COLORS.text, fontWeight: '900', fontSize: 15 },
  itemMeta: { marginTop: 3, color: COLORS.muted, fontWeight: '700' },
  itemAmount: { color: COLORS.text, fontWeight: '900', fontSize: 13 },
  categoryPill: { paddingHorizontal: 9, paddingVertical: 6, borderRadius: 999, backgroundColor: COLORS.skySoft, borderWidth: 1, borderColor: '#BFEAF2' },
  categoryPillText: { color: COLORS.sky, fontWeight: '900', fontSize: 11 },
  skippedPill: { paddingHorizontal: 9, paddingVertical: 6, borderRadius: 999, backgroundColor: PADIPRO.goldDeep, borderWidth: 1, borderColor: PADIPRO.goldDeep },
  skippedPillText: { color: '#FFFFFF', fontWeight: '900', fontSize: 11 },
  duplicateHeroBox: { marginTop: 12, padding: 13, borderRadius: 18, backgroundColor: PADIPRO.goldSoft, borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  duplicateHeroIcon: { width: 44, height: 44, borderRadius: 16, backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', alignItems: 'center', justifyContent: 'center' },
  duplicateHeroTitle: { color: '#5F4610', fontWeight: '900', fontSize: 15 },
  duplicateHeroText: { marginTop: 4, color: '#765B21', fontWeight: '800', fontSize: 12.5, lineHeight: 18 },
  duplicateOutcomeRow: { marginTop: 9, flexDirection: 'row', gap: 8 },
  duplicateOutcomeCell: { flex: 1, minHeight: 40, borderRadius: 14, backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingHorizontal: 8 },
  duplicateOutcomeText: { color: PADIPRO.goldDeep, fontWeight: '900', fontSize: 11.5, textAlign: 'center' },
  lockedDecisionStrip: { marginTop: 10, padding: 10, borderRadius: 15, backgroundColor: '#F7EFD4', borderWidth: 1, borderColor: '#E3CF91', flexDirection: 'row', alignItems: 'center', gap: 7 },
  lockedDecisionText: { flex: 1, color: PADIPRO.goldDeep, fontWeight: '800', fontSize: 12, lineHeight: 17 },
  decisionStrip: { marginTop: 12, padding: 11, borderRadius: 16, backgroundColor: '#FEFCF4', borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', flexDirection: 'row', alignItems: 'center', gap: 10 },
  decisionIcon: { width: 32, height: 32, borderRadius: 12, backgroundColor: '#fff', borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', alignItems: 'center', justifyContent: 'center' },
  decisionTitle: { color: COLORS.text, fontWeight: '900', fontSize: 13 },
  decisionSub: { color: COLORS.muted, fontWeight: '700', marginTop: 2, fontSize: 11.5 },
  matchBox: { marginTop: 11, padding: 10, borderRadius: 15, backgroundColor: PADIPRO.surfaceSunken, borderWidth: 1, borderColor: PADIPRO.border, flexDirection: 'row', alignItems: 'center', gap: 7 },
  matchBoxWarning: { backgroundColor: COLORS.goldSoft, borderColor: 'rgba(166,124,0,0.32)' },
  matchText: { flex: 1, color: COLORS.greenDark, fontWeight: '800', fontSize: 12.5 },
  scanDuplicateBox: { marginTop: 10, padding: 11, borderRadius: 16, backgroundColor: COLORS.goldSoft, borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', flexDirection: 'row', alignItems: 'flex-start', gap: 9 },
  scanDuplicateIcon: { width: 32, height: 32, borderRadius: 12, backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', alignItems: 'center', justifyContent: 'center' },
  scanDuplicateTitle: { color: PADIPRO.goldDeep, fontWeight: '900', fontSize: 13 },
  scanDuplicateText: { marginTop: 3, color: PADIPRO.goldDeep, fontWeight: '800', fontSize: 11.5, lineHeight: 16 },
  productDecisionBox: { marginTop: 11, padding: 12, borderRadius: 18, backgroundColor: '#F9FBF6', borderWidth: 1, borderColor: PADIPRO.border },
  productDecisionHead: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  productDecisionIcon: { width: 34, height: 34, borderRadius: 13, backgroundColor: '#fff', borderWidth: 1, borderColor: PADIPRO.border, alignItems: 'center', justifyContent: 'center' },
  productDecisionTitle: { color: COLORS.text, fontWeight: '900', fontSize: 14 },
  productDecisionSub: { marginTop: 3, color: COLORS.muted, fontWeight: '700', fontSize: 11.5, lineHeight: 16 },
  reviewBadge: { minHeight: 28, borderRadius: 999, paddingHorizontal: 9, backgroundColor: COLORS.goldSoft, borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', flexDirection: 'row', alignItems: 'center', gap: 4 },
  reviewBadgeDone: { backgroundColor: COLORS.greenSoft, borderColor: PADIPRO.border },
  reviewBadgeText: { color: PADIPRO.goldDeep, fontWeight: '900', fontSize: 11 },
  reviewBadgeTextDone: { color: COLORS.greenDark },
  productModeRow: { marginTop: 11, flexDirection: 'row', gap: 8 },
  productModeBtn: { flex: 1, minHeight: 42, borderRadius: 15, borderWidth: 1, borderColor: PADIPRO.border, backgroundColor: '#fff', alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 6, paddingHorizontal: 8 },
  productModeBtnActive: { backgroundColor: COLORS.greenDark, borderColor: COLORS.greenDark },
  productModeBtnActiveNew: { backgroundColor: COLORS.gold, borderColor: '#8D681D' },
  productModeBtnDisabled: { opacity: 0.45 },
  productModeText: { color: COLORS.greenDark, fontWeight: '900', fontSize: 11.8 },
  productModeTextActive: { color: '#fff' },
  existingTargetBox: { marginTop: 10, borderRadius: 15, padding: 11, backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: PADIPRO.border },
  existingTargetLabel: { color: COLORS.muted, fontWeight: '900', fontSize: 10.8, textTransform: 'uppercase', letterSpacing: 0.25 },
  existingTargetName: { marginTop: 3, color: COLORS.text, fontWeight: '900', fontSize: 14 },
  existingTargetMeta: { marginTop: 3, color: COLORS.greenDark, fontWeight: '800', fontSize: 11.5, lineHeight: 16 },
  newProductBox: { marginTop: 10, borderRadius: 15, padding: 11, backgroundColor: COLORS.goldSoft, borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)' },
  newProductTitle: { color: PADIPRO.goldDeep, fontWeight: '900', fontSize: 13 },
  newProductDesc: { marginTop: 3, color: PADIPRO.goldDeep, fontWeight: '800', fontSize: 11.5, lineHeight: 16 },
  productSearchBox: { marginTop: 11 },
  productSearchLabel: { color: COLORS.text, fontWeight: '900', fontSize: 12, marginBottom: 6 },
  productSearchInput: { minHeight: 42, borderWidth: 1, borderColor: PADIPRO.border, backgroundColor: '#fff', borderRadius: 14, paddingHorizontal: 11, paddingVertical: 9, color: COLORS.text, fontWeight: '800' },
  productResultList: { marginTop: 8, borderWidth: 1, borderColor: PADIPRO.border, borderRadius: 15, overflow: 'hidden', backgroundColor: '#fff' },
  productResultRow: { minHeight: 52, paddingHorizontal: 11, paddingVertical: 9, borderTopWidth: 1, borderTopColor: PADIPRO.surfaceSunken, flexDirection: 'row', alignItems: 'center', gap: 8 },
  productResultName: { color: COLORS.text, fontWeight: '900', fontSize: 12.5 },
  productResultMeta: { marginTop: 2, color: COLORS.muted, fontWeight: '700', fontSize: 11 },
  actionCaption: { marginTop: 12, color: COLORS.text, fontWeight: '900', fontSize: 12.5 },
  actionGrid: { marginTop: 11, flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  actionBtn: { flexGrow: 1, minWidth: '46%', borderWidth: 1, borderColor: PADIPRO.border, backgroundColor: '#fff', borderRadius: 16, paddingVertical: 10, paddingHorizontal: 10, flexDirection: 'row', alignItems: 'center', gap: 8 },
  actionBtnActive: { backgroundColor: COLORS.green, borderColor: COLORS.greenDark },
  actionLabel: { color: COLORS.greenDark, fontWeight: '900', fontSize: 12.5 },
  actionLabelActive: { color: '#fff' },
  actionHint: { color: COLORS.muted, fontWeight: '700', fontSize: 10.5, marginTop: 2 },
  actionHintActive: { color: 'rgba(255,255,255,0.82)' },
  splitBox: { marginTop: 11, flexDirection: 'row', gap: 10, padding: 10, borderRadius: 16, backgroundColor: '#FFFCF2', borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)' },
  inputLabel: { color: COLORS.muted, fontWeight: '900', fontSize: 12, marginBottom: 6 },
  input: { borderWidth: 1, borderColor: PADIPRO.border, backgroundColor: '#fff', borderRadius: 12, paddingHorizontal: 11, paddingVertical: 9, color: COLORS.text, fontWeight: '800' },
  fieldAssignBox: { marginTop: 12, padding: 12, borderRadius: 18, backgroundColor: PADIPRO.surfaceSunken, borderWidth: 1, borderColor: PADIPRO.border },
  fieldAssignHead: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  fieldAssignTitle: { color: COLORS.text, fontWeight: '900', fontSize: 14 },
  fieldAssignSub: { marginTop: 3, color: COLORS.muted, fontWeight: '700', fontSize: 11.5 },
  fieldRefreshBtn: { width: 34, height: 34, borderRadius: 13, backgroundColor: '#fff', borderWidth: 1, borderColor: PADIPRO.border, alignItems: 'center', justifyContent: 'center' },
  fieldOpenBtn: { minHeight: 36, borderRadius: 14, paddingHorizontal: 12, backgroundColor: '#fff', borderWidth: 1, borderColor: PADIPRO.border, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5 },
  fieldOpenText: { color: COLORS.greenDark, fontWeight: '900', fontSize: 12 },
  fieldCompactRow: { marginTop: 10, flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  fieldMethodBadge: { borderRadius: 999, paddingHorizontal: 9, paddingVertical: 6, backgroundColor: '#EEF8EF', borderWidth: 1, borderColor: PADIPRO.border, flexDirection: 'row', alignItems: 'center', gap: 5 },
  fieldMethodBadgeText: { color: COLORS.greenDark, fontWeight: '900', fontSize: 11 },
  selectedPreviewRow: { flex: 1, minWidth: 160, flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  selectedPreviewPill: { maxWidth: 108, borderRadius: 999, paddingHorizontal: 9, paddingVertical: 6, backgroundColor: '#fff', borderWidth: 1, borderColor: PADIPRO.border },
  selectedPreviewText: { color: COLORS.text, fontWeight: '800', fontSize: 11 },
  selectedMorePill: { borderRadius: 999, paddingHorizontal: 9, paddingVertical: 6, backgroundColor: COLORS.greenDark },
  selectedMoreText: { color: '#fff', fontWeight: '900', fontSize: 11 },
  fieldCompactHint: { color: COLORS.muted, fontWeight: '800', fontSize: 11.5 },
  methodRow: { marginTop: 11, flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  methodChip: { borderRadius: 999, paddingHorizontal: 10, paddingVertical: 7, backgroundColor: '#fff', borderWidth: 1, borderColor: PADIPRO.border },
  methodChipActive: { backgroundColor: COLORS.greenDark, borderColor: COLORS.greenDark },
  methodChipText: { color: COLORS.greenDark, fontWeight: '900', fontSize: 11.5 },
  methodChipTextActive: { color: '#fff' },
  miniActionBtn: { borderRadius: 999, paddingHorizontal: 10, paddingVertical: 7, backgroundColor: COLORS.greenSoft, borderWidth: 1, borderColor: '#BFE0C5' },
  miniActionText: { color: COLORS.greenDark, fontWeight: '900', fontSize: 11.5 },
  miniGhostBtn: { borderRadius: 999, paddingHorizontal: 10, paddingVertical: 7, backgroundColor: '#fff', borderWidth: 1, borderColor: PADIPRO.border },
  miniGhostText: { color: COLORS.muted, fontWeight: '900', fontSize: 11.5 },
  fieldChipGrid: { marginTop: 10, flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  fieldChip: { flexGrow: 1, minWidth: '47%', minHeight: 54, borderRadius: 16, paddingHorizontal: 10, paddingVertical: 9, backgroundColor: '#fff', borderWidth: 1, borderColor: PADIPRO.border, flexDirection: 'row', alignItems: 'center', gap: 8 },
  fieldChipActive: { backgroundColor: COLORS.green, borderColor: COLORS.greenDark },
  fieldChipName: { color: COLORS.text, fontWeight: '900', fontSize: 12.5 },
  fieldChipNameActive: { color: '#fff' },
  fieldChipMeta: { marginTop: 2, color: COLORS.muted, fontWeight: '700', fontSize: 10.5 },
  fieldChipMetaActive: { color: 'rgba(255,255,255,0.82)' },
  fieldEmptyBox: { marginTop: 10, minHeight: 46, borderRadius: 14, backgroundColor: '#fff', borderWidth: 1, borderColor: PADIPRO.border, alignItems: 'center', justifyContent: 'center', padding: 10 },
  fieldEmptyText: { color: COLORS.muted, fontWeight: '800', textAlign: 'center' },
  confirmBtn: { marginTop: 16, minHeight: 54, borderRadius: 18, backgroundColor: COLORS.green, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 9, ...shadow },
  confirmText: { color: '#fff', fontWeight: '900', fontSize: 16 },
  footnote: { marginTop: 12, color: COLORS.muted, fontWeight: '700', textAlign: 'center', lineHeight: 19 },
  noticeMask: { flex: 1, backgroundColor: 'rgba(11,31,22,0.50)', padding: 18, alignItems: 'center', justifyContent: 'center' },
  noticeCard: { width: '100%', maxWidth: 430, borderRadius: 28, padding: 18, backgroundColor: PADIPRO.goldSoft, borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', alignItems: 'center', ...shadow },
  noticeIconWrap: { width: 70, height: 70, borderRadius: 26, backgroundColor: COLORS.goldSoft, borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  noticeTitle: { color: COLORS.text, fontWeight: '900', fontSize: 20, letterSpacing: -0.3, textAlign: 'center' },
  noticeSub: { marginTop: 7, color: COLORS.muted, fontWeight: '800', lineHeight: 20, textAlign: 'center' },
  noticeList: { width: '100%', marginTop: 14, borderRadius: 18, backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: COLORS.border, overflow: 'hidden' },
  noticeListRow: { minHeight: 56, paddingHorizontal: 11, paddingVertical: 10, borderTopWidth: 1, borderTopColor: PADIPRO.surfaceSunken, flexDirection: 'row', alignItems: 'center', gap: 9 },
  noticeListNo: { width: 28, height: 28, borderRadius: 10, backgroundColor: COLORS.goldSoft, borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', alignItems: 'center', justifyContent: 'center' },
  noticeListNoText: { color: PADIPRO.goldDeep, fontWeight: '900', fontSize: 12 },
  noticeListName: { color: COLORS.text, fontWeight: '900', fontSize: 12.5, lineHeight: 17 },
  noticeListMeta: { marginTop: 2, color: PADIPRO.goldDeep, fontWeight: '800', fontSize: 11.5 },
  noticeMore: { padding: 11, color: COLORS.muted, fontWeight: '900', fontSize: 12 },
  noticePrimaryBtn: { width: '100%', minHeight: 50, marginTop: 15, borderRadius: 17, backgroundColor: COLORS.greenDark, alignItems: 'center', justifyContent: 'center' },
  noticePrimaryText: { color: '#FFFFFF', fontWeight: '900', fontSize: 15 },
  successMask: { flex: 1, backgroundColor: 'rgba(11,31,22,0.48)', padding: 18, alignItems: 'center', justifyContent: 'center' },
  successCard: { width: '100%', maxWidth: 430, maxHeight: '92%', borderRadius: 30, padding: 18, backgroundColor: PADIPRO.goldSoft, borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', alignItems: 'center', ...shadow },
  successIconWrap: { width: 74, height: 74, borderRadius: 28, backgroundColor: COLORS.greenSoft, borderWidth: 1, borderColor: PADIPRO.border, alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  successTitle: { color: COLORS.text, fontWeight: '900', fontSize: 21, letterSpacing: -0.35, textAlign: 'center' },
  successSub: { marginTop: 7, color: COLORS.muted, fontWeight: '700', lineHeight: 20, textAlign: 'center' },
  successStats: { width: '100%', flexDirection: 'row', gap: 10, marginTop: 16 },
  successStatBox: { flex: 1, minHeight: 78, borderRadius: 19, padding: 12, backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: COLORS.border, justifyContent: 'center' },
  successStatLabel: { color: COLORS.muted, fontWeight: '900', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.25 },
  successStatValue: { marginTop: 5, color: COLORS.greenDark, fontWeight: '900', fontSize: 16 },
  successNote: { width: '100%', marginTop: 12, borderRadius: 18, padding: 12, backgroundColor: COLORS.goldSoft, borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  successNoteText: { flex: 1, color: PADIPRO.goldDeep, fontWeight: '800', lineHeight: 18, fontSize: 12.5 },
  duplicateNotice: { width: '100%', marginTop: 12, borderRadius: 18, padding: 12, backgroundColor: PADIPRO.goldSoft, borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', flexDirection: 'row', alignItems: 'flex-start', gap: 9 },
  duplicateNoticeIcon: { width: 32, height: 32, borderRadius: 12, backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: 'rgba(166,124,0,0.32)', alignItems: 'center', justifyContent: 'center' },
  duplicateNoticeTitle: { color: PADIPRO.goldDeep, fontWeight: '900', fontSize: 13 },
  duplicateNoticeText: { marginTop: 2, color: PADIPRO.goldDeep, fontWeight: '800', fontSize: 11.5, lineHeight: 16 },
  processedPanel: { width: '100%', marginTop: 12, borderRadius: 18, padding: 12, backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: COLORS.border },
  processedPanelTitle: { color: COLORS.text, fontWeight: '900', fontSize: 13.5, marginBottom: 8 },
  processedRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 9, paddingVertical: 8, borderTopWidth: 1, borderTopColor: PADIPRO.surfaceSunken },
  processedIcon: { width: 30, height: 30, borderRadius: 12, backgroundColor: COLORS.greenSoft, borderWidth: 1, borderColor: PADIPRO.border, alignItems: 'center', justifyContent: 'center' },
  processedTitle: { color: COLORS.greenDark, fontWeight: '900', fontSize: 12.5 },
  processedDesc: { marginTop: 2, color: COLORS.muted, fontWeight: '700', fontSize: 11.5, lineHeight: 16 },
  processedMore: { marginTop: 7, color: COLORS.muted, fontWeight: '900', fontSize: 11.5 },
  successActions: { width: '100%', flexDirection: 'row', gap: 10, marginTop: 16 },
  successBtn: { flex: 1, minHeight: 48, borderRadius: 17, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 10 },
  successGhost: { backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: PADIPRO.border },
  successPrimary: { backgroundColor: COLORS.greenDark, borderWidth: 1, borderColor: COLORS.greenDark },
  successGhostText: { color: COLORS.greenDark, fontWeight: '900' },
  successPrimaryText: { color: '#FFFFFF', fontWeight: '900' },
});
